import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { ConfirmationDialog, PageHeader, useToast } from 'components/admin';
import { Button, Input, Select, Table } from 'components/common';
import { deleteNews, getAdminNews } from 'apis/adminNews';
import { getNewsCategories } from 'apis/newsCategories';
import { useActiveLanguagesQuery, useAdminDeleteMutation, useAdminListQuery } from 'hooks';
import '../../../styles/admin-shared.scss';

const NewsList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [keyword, setKeyword] = useState('');
    const [isActiveFilter, setIsActiveFilter] = useState('');
    const [languageCodeFilter, setLanguageCodeFilter] = useState('');
    const [deleteId, setDeleteId] = useState(null);
    const toast = useToast();
    const navigate = useNavigate();

    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: categoriesData = { items: [] } } = useAdminListQuery('news-categories-options', { pageSize: 1000, languageCode: languageCodeFilter || 'vi' }, getNewsCategories);
    const categories = categoriesData.items;
    const queryParams = useMemo(() => ({
        pageIndex: currentPage,
        pageSize,
        keyword: keyword || undefined,
        isActive: isActiveFilter === '' ? undefined : isActiveFilter === 'true',
        languageCode: languageCodeFilter || undefined,
    }), [currentPage, pageSize, keyword, isActiveFilter, languageCodeFilter]);
    const { data = { items: [], total: 0, currentPage, pageSize }, isFetching, error } = useAdminListQuery('news', queryParams, getAdminNews);
    const deleteMutation = useAdminDeleteMutation('news', deleteNews);

    useEffect(() => { setCurrentPage(1); }, [keyword, isActiveFilter, languageCodeFilter]);
    useEffect(() => { if (error) toast.error(error.message || 'T?i d? li?u th?t b?i'); }, [error, toast]);

    const handleDelete = async () => {
        try {
            const res = await deleteMutation.mutateAsync(deleteId);
            if (res.success) { toast.success('Xóa thành công'); }
            else toast.error(res.message);
        } catch { toast.error('Xóa thất bại'); }
        setDeleteId(null);
    };

    const columns = [
        { field: 'id', header: 'ID' },
        { header: 'Hình ảnh', render: (row) => row.thumbnail ? <img src={row.thumbnail} alt="" style={{ height: 40, objectFit: 'contain' }} /> : '—' },
        { header: 'Tiêu đề', render: (row) => row.title || row.translations?.[0]?.title || '—' },
        { header: 'Danh mục', render: (row) => {
            const cat = categories.find(c => c.id === row.newsCategoryId);
            const activeLangId = languages.find(l => l.code === (languageCodeFilter || 'vi'))?.id;
            const t = cat?.translations?.find(tr => tr.languageId === activeLangId) || cat?.translations?.[0];
            return t?.title || cat?.title || `ID: ${row.newsCategoryId}`;
        } },
        { header: 'Trạng thái', render: (row) => <span className={`status-badge ${row.isActive ? 'active' : 'inactive'}`}>{row.isActive ? 'Hoạt động' : 'Không hoạt động'}</span> },
        { field: 'sortOrder', header: 'Thứ tự' },
        {
            header: 'Thao tác', render: (row) => (
                <div className="action-buttons">
                    <button className="icon-btn icon-btn--edit" onClick={() => navigate(`/admin/news/edit/${row.id}`)} title="Sửa"><FiEdit2 size={15} /></button>
                    <button className="icon-btn icon-btn--delete" onClick={() => setDeleteId(row.id)} title="Xóa"><FiTrash2 size={15} /></button>
                </div>
            )
        },
    ];

    return (
        <div className="admin-page">
            <PageHeader
                title="Tin Tức"
                subtitle="Quản lý bài viết tin tức"
                action={<Button onClick={() => navigate('/admin/news/create')}>+ Thêm Bài Viết</Button>}
            />
            <div className="filters-bar">
                <div className="filter-item"><Input placeholder="Tìm kiếm..." value={keyword} onChange={e => setKeyword(e.target.value)} /></div>
                <div className="filter-item">
                    <Select value={isActiveFilter} onChange={e => setIsActiveFilter(e.target.value)}
                        options={[{ value: '', label: 'Tất cả trạng thái' }, { value: 'true', label: 'Hoạt động' }, { value: 'false', label: 'Không hoạt động' }]} />
                </div>
                <div className="filter-item">
                    <Select value={languageCodeFilter} onChange={(e) => setLanguageCodeFilter(e.target.value)}
                        options={[...languages.map(l => ({ value: l.code, label: l.name }))]} />
                </div>
            </div>
            <Table
                columns={columns}
                data={data.items}
                loading={isFetching}
                pagination={{ currentPage: data.currentPage, totalPages: Math.ceil(data.total / data.pageSize) || 1, onPageChange: setCurrentPage }}
            />
            <ConfirmationDialog open={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} title="Xóa Tin Tức" message="Bạn có chắc chắn muốn xóa bài viết này?" />
        </div>
    );
};

export default NewsList;

